import React from "react"
import {useTranslation} from "react-i18next"
import {Formik, Form, FormikProps, FormikValues, Field, FieldProps} from "formik"
import {CircularProgress} from "@material-ui/core"

// HOCs
import withSignIn, {WithSignInProps} from "../../../hoc/withSignIn"

// Models
import {SignInCredentials} from "../../../models"

// Utils
import Utils from "../../../../../utils/Utils"

// Styles
import useStyles from "./styles"

type ModernFieldConfig = {
    id: string
    name: string
    type: string
    labelKey: string
    placeholder: string
    autoComplete: string
    validate(value: string): string | undefined
}

const FIELDS: ModernFieldConfig[] = [
    {
        id: "email",
        name: "username",
        type: "email",
        labelKey: "sign_in.form.label.email",
        placeholder: "dr.smith@clinic.com",
        autoComplete: "username",
        validate: Utils.validateEmail,
    },
    {
        id: "password",
        name: "password",
        type: "password",
        labelKey: "sign_in.form.label.password",
        placeholder: "••••••••",
        autoComplete: "current-password",
        validate: Utils.validatePassword,
    },
];

/**
 * Modern sign-in card.
 *
 * Deliberately shares everything but the markup with the Classic
 * <SignInForm/>: the same `withSignIn` HOC, the same `Utils.validateEmail` /
 * `Utils.validatePassword` Formik validators, the same "show the error once the
 * field is touched or the form was submitted" rule, and the same
 * `signInErrorMessage` translation key. Only the presentation is the
 * prototype's `.login-card`.
 *
 * @param props
 */
function ModernSignInForm(props: WithSignInProps) {
    const styles = useStyles();
    const [t] = useTranslation("common");
    const {signIn, signingIn, signInErrorMessage} = props;

    function handleSubmit(values: SignInCredentials) {
        if (signingIn) return

        signIn(values);
    }

    return (
      <Formik onSubmit={handleSubmit} initialValues={{username: "", password: ""}}>
          {(formProps: FormikProps<FormikValues>) => {
              const {isValid, handleSubmit} = formProps;

              /*
               * No wordmark on the card. <ModernSignInPage/> already puts one on
               * the brand panel, which is on screen at every width now, and two
               * logos a hand's width apart is one too many.
               */
              return (
                <Form onSubmit={handleSubmit} noValidate className={styles.card}>
                    <h1 className={styles.title}>{t("sign_in.title")}</h1>
                    <p className={styles.sub}>{t("modern.welcome_back", "Welcome back")}</p>

                    {FIELDS.map(config => (
                      <Field key={config.name} type={config.type} name={config.name} validate={config.validate}>
                          {(fieldProps: FieldProps) => {
                              const {field, form: {touched, errors, submitCount}} = fieldProps;
                              const error = errors[field.name];
                              const submitted = submitCount > 0;
                              const fieldTouched = touched[field.name] as boolean;
                              const touchedError = hasError() && fieldTouched;
                              const submittedError = hasError() && submitted;
                              const showError = touchedError || submittedError;

                              function hasError() {
                                  return typeof error !== "undefined"
                              }

                              return (
                                <label className={styles.field} htmlFor={config.id}>
                                    <span className={styles.field_label}>{t(config.labelKey)}</span>
                                    <input
                                      {...field}
                                      id={config.id}
                                      type={config.type}
                                      required
                                      autoComplete={config.autoComplete}
                                      placeholder={config.placeholder}
                                      aria-invalid={showError}
                                      className={`${styles.input} ${showError ? styles.input__error : ""}`}
                                    />
                                    {showError && (
                                      <span className={styles.field_error}>{t(error as string)}</span>
                                    )}
                                </label>
                              )
                          }}
                      </Field>
                    ))}

                    {signInErrorMessage !== null && (
                      <div className={styles.sign_in_error} role="alert">{t(signInErrorMessage)}</div>
                    )}

                    <button type="submit" className={styles.submit_button} disabled={!isValid || signingIn}>
                        {signingIn && (
                          <CircularProgress size={15} color="inherit" className={styles.submit_spinner}/>
                        )}
                        {t("sign_in.submit")}
                    </button>
                </Form>
              )
          }}
      </Formik>
    )
}

export {ModernSignInForm}

export default withSignIn(ModernSignInForm)
